/// Goal-driven spawner. Decides where in world-space to drop a new
/// aircraft, what heading/altitude/speed to give it, and (for takeoff
/// goals) the initial ground state.
///
/// Mirrors the Swift logic at FlatoutATC/Support/AircraftSpawner.swift
/// (scenario branch + goal-based fallback). The carrier-aircraft and
/// SID/STAR branches are deferred — N51 doesn't use them and they
/// pull in the AIController machinery.
///
/// All math in world-pixel space: the projector is the source of truth
/// for converting coordinates and we use level.worldSizeInNM to pick
/// edge-of-radar spawn points.

import type { CoordinateProjector } from '../geo/CoordinateProjector'
import type {
  Airport,
  AircraftGoal,
  AircraftType,
  LevelData,
  Runway,
} from '../types/LevelData'
import { performanceFor } from './AircraftDatabase'
import { randInRange, randIntInclusive, type RandomSource } from './rng'
import type { AircraftStatus } from './Aircraft'

export interface SpawnContext {
  level: LevelData
  projector: CoordinateProjector
  pxPerNM: number
  worldPx: number
  rng: RandomSource
  /// Live simulated wind direction (degrees magnetic) — used by
  /// the takeoff runway picker so a fresh awaiting-takeoff aircraft
  /// queues at the runway end the wind currently favours, not
  /// whichever end the level's `fixedWindDirection` config
  /// preferred at startup.
  effectiveWindDirection: number
}

export interface SpawnResult {
  position: { x: number; y: number }
  headingDeg: number
  altitudeFt: number
  speedKts: number
  status: AircraftStatus
  /// Set for goals that need to chase a fix immediately (transition,
  /// takeoff-after-rotation). Recorded so the autopilot can steer.
  initialDirectTo?: { point: { x: number; y: number }; name: string }
  /// Recorded for takeoff aircraft so the UI/log can show context.
  departureRunway?: { airportName: string; runwayId: string }
}

const SPAWN_RADIUS_NM_MAX = 30
/// Spawn altitudes are forced at least this many feet above the
/// highest terrain polygon in the level. Without it, the random
/// edge spawn would occasionally drop an aircraft inside (or below)
/// a peak's polygon — the next collision sweep would mark it
/// crashed before the player could even see it. iOS handles this by
/// hand-tuning `minimumSpawnAltitude` per level; we automate it as
/// a safety floor while still respecting the per-level value when
/// it's higher.
const TERRAIN_BUFFER_FT = 1000

/// Resolve a goal into a runtime SpawnResult. Returns null when the
/// goal references unknown waypoints/airports — caller should skip
/// the spawn and log.
export function spawnByGoal(
  ctx: SpawnContext,
  type: AircraftType,
  goal: AircraftGoal,
): SpawnResult | null {
  if (goal.takeoff) return spawnTakeoff(ctx, type, goal.takeoff.airportName)
  if (goal.departure)
    return spawnDeparture(ctx, type, goal.departure.airportName, goal.departure.runwayId)
  if (goal.arrival) return spawnArrival(ctx, type, goal.arrival)
  if (goal.transition) return spawnEdgeForTransition(ctx, type, goal.transition.waypointName)
  if (goal.survey) {
    // Survey aircraft enter the radar like a transition — edge spawn
    // aimed at the circle waypoint, then `tickSurvey` takes over once
    // they arrive (engage hold → tick timer → route to exit fix).
    return spawnEdgeForTransition(ctx, type, goal.survey.circleWaypointName)
  }
  if (goal.land) return spawnEdgeForLanding(ctx, type, goal.land.airportName)
  return null
}

/// SID departure: same threshold-spawn as a takeoff goal, but the
/// runway is fixed by the SID (rather than wind-driven). The flight
/// plan itself is engaged when the player issues `commandTakeoff`.
function spawnDeparture(
  ctx: SpawnContext,
  _type: AircraftType,
  airportName: string,
  runwayId: string,
): SpawnResult | null {
  const airport = findAirport(ctx.level, airportName)
  if (!airport) return null
  // Find the runway end matching the SID's published runway id.
  for (const runway of airport.runways) {
    if (runway.id1 === runwayId) {
      return makeThresholdSpawn(ctx, airport, runway, runway.heading1, runwayId)
    }
    if (runway.id2 === runwayId) {
      return makeThresholdSpawn(ctx, airport, runway, runway.heading2, runwayId)
    }
  }
  return null
}

/// STAR arrival: edge spawn aimed at the STAR's first leg waypoint
/// (rather than the airport itself). The flight plan is engaged at
/// the spawn site so the aircraft auto-flies the procedure.
function spawnArrival(
  ctx: SpawnContext,
  type: AircraftType,
  arrival: { airportName: string; starName: string; runwayId?: string },
): SpawnResult | null {
  const airport = findAirport(ctx.level, arrival.airportName)
  if (!airport) return null
  const star = airport.stars?.find((s) => s.name === arrival.starName)
  const firstLegName = star?.legs[0]?.waypointName
  const firstWp = firstLegName
    ? ctx.level.waypoints.find((w) => w.name === firstLegName)
    : null
  // No STAR data or unknown first waypoint — fall back to a plain
  // airport-edge spawn so the level isn't broken.
  if (!firstWp || !firstLegName) {
    return spawnOnEdge(ctx, type, airport.centerCoordinate)
  }
  const result = spawnOnEdge(ctx, type, firstWp.coordinate)
  if (!result) return null
  // Aim the heading at the first waypoint; record it as the
  // initial directTo so the autopilot has something to track until
  // the flight-plan tick takes over.
  const wpPx = ctx.projector.project(firstWp.coordinate, true)
  result.headingDeg = headingFromTo(result.position, wpPx)
  result.initialDirectTo = { point: wpPx, name: firstLegName }
  return result
}

/// Shared threshold-spawn body used by both takeoff and SID-departure
/// goals.
function makeThresholdSpawn(
  ctx: SpawnContext,
  airport: Airport,
  runway: Runway,
  headingDeg: number,
  runwayId: string,
): SpawnResult {
  const thresholdGeo =
    headingDeg === runway.heading1 ? runway.startCoordinate : runway.endCoordinate
  const threshold = ctx.projector.project(thresholdGeo, true)
  return {
    position: threshold,
    headingDeg,
    altitudeFt: airport.elevation ?? 0,
    speedKts: 0,
    status: 'awaitingTakeoff',
    departureRunway: { airportName: airport.name, runwayId },
  }
}

/// Drop the aircraft at the active runway threshold of the requested
/// airport, on the ground awaiting a takeoff command. The MVP picks
/// the first runway whose end is closest to (or matches) the level's
/// fixedWindDirection; if no wind, it just picks heading1 of the
/// first runway.
function spawnTakeoff(ctx: SpawnContext, _type: AircraftType, airportName: string): SpawnResult | null {
  const airport = findAirport(ctx.level, airportName)
  if (!airport || airport.runways.length === 0) return null
  const pick = pickActiveRunwayEnd(airport, ctx.effectiveWindDirection)
  if (!pick) return null
  const runwayId =
    pick.headingDeg === pick.runway.heading1 ? pick.runway.id1 : pick.runway.id2
  return makeThresholdSpawn(ctx, airport, pick.runway, pick.headingDeg, runwayId)
}

function spawnEdgeForLanding(
  ctx: SpawnContext,
  type: AircraftType,
  airportName: string,
): SpawnResult | null {
  const airport = findAirport(ctx.level, airportName)
  if (!airport) return null
  return spawnOnEdge(ctx, type, airport.centerCoordinate)
}

function spawnEdgeForTransition(
  ctx: SpawnContext,
  type: AircraftType,
  waypointName: string,
): SpawnResult | null {
  const wp = ctx.level.waypoints.find((w) => w.name === waypointName)
  if (!wp) return null
  const result = spawnOnEdge(ctx, type, wp.coordinate)
  if (!result) return null
  // Aim at the waypoint and remember it as the directTo target.
  const wpPx = ctx.projector.project(wp.coordinate, true)
  result.initialDirectTo = { point: wpPx, name: waypointName }
  result.headingDeg = headingFromTo(result.position, wpPx)
  return result
}

/// Drop on a random world-edge, oriented inward, with a type-appropriate
/// random altitude and speed. If `aimAt` is supplied, the aircraft is
/// pointed toward that geographic point; otherwise we use a generic
/// inward heading (edge-dependent).
function spawnOnEdge(
  ctx: SpawnContext,
  type: AircraftType,
  aimAt: import('../types/LevelData').GeoCoordinate,
): SpawnResult {
  const halfWorld = ctx.worldPx / 2
  const maxRadiusPx = SPAWN_RADIUS_NM_MAX * ctx.pxPerNM
  const r = Math.min(halfWorld, maxRadiusPx)
  // Edge: 0=top, 1=right, 2=bottom, 3=left (top is screen-top, i.e. -y).
  const edge = randIntInclusive(ctx.rng, 0, 3)
  const cx = ctx.worldPx / 2
  const cy = ctx.worldPx / 2
  let position: { x: number; y: number }
  switch (edge) {
    case 0: // top
      position = { x: cx + randInRange(ctx.rng, -r, r), y: cy - r }
      break
    case 1: // right
      position = { x: cx + r, y: cy + randInRange(ctx.rng, -r, r) }
      break
    case 2: // bottom
      position = { x: cx + randInRange(ctx.rng, -r, r), y: cy + r }
      break
    default: // left
      position = { x: cx - r, y: cy + randInRange(ctx.rng, -r, r) }
      break
  }

  const aimPx = ctx.projector.project(aimAt, true)
  const headingDeg = headingFromTo(position, aimPx)

  const perf = performanceFor(type)
  // Helicopters have `minSpeedKts: 0` so the Hover command can clamp
  // target speed to a stationary hover. We don't want a fresh helo
  // to *spawn* at 0 kt 30 NM out — the player would have nothing to
  // vector. Per-spawn floor of 60 kt mirrors iOS AircraftSpawner.swift.
  const spawnMinSpeed =
    type === 'helicopter' ? Math.max(60, perf.minSpeedKts) : perf.minSpeedKts
  const speedKts = randInRange(ctx.rng, spawnMinSpeed, perf.maxSpeedKts)
  // Altitude: integer thousands within [min, max], floored at the
  // level's MSA. Three sources contribute to the floor; we take the
  // highest:
  //   1. Type's own minimum (e.g., a regional jet won't fly at 1k).
  //   2. Per-level hand-tuned `minimumSpawnAltitude` (Innsbruck = 12k).
  //   3. Auto-derived: max terrain polygon elevation + a buffer so a
  //      random edge spawn never drops the aircraft inside Mauna Kea.
  const terrainFloor =
    maxTerrainElevationFt(ctx.level) > 0
      ? maxTerrainElevationFt(ctx.level) + TERRAIN_BUFFER_FT
      : 0
  const floor = Math.max(
    perf.minAltitudeFt,
    ctx.level.minimumSpawnAltitude ?? 0,
    terrainFloor,
  )
  const minStep = Math.ceil(floor / 1000)
  const maxStep = Math.floor(perf.maxAltitudeFt / 1000)
  const altitudeFt = randIntInclusive(ctx.rng, Math.min(minStep, maxStep), maxStep) * 1000

  return { position, headingDeg, altitudeFt, speedKts, status: 'enRoute' }
}

/// Highest terrain polygon elevation in the level, or 0 if none.
/// Memoised per level via WeakMap so a 30-aircraft spawn pass
/// doesn't reduce the polygon list 30 times.
const maxTerrainCache = new WeakMap<LevelData, number>()
export function maxTerrainElevationFt(level: LevelData): number {
  const cached = maxTerrainCache.get(level)
  if (cached !== undefined) return cached
  const obstacles = level.terrainObstacles ?? []
  let max = 0
  for (const t of obstacles) {
    if (t.elevationInFeet > max) max = t.elevationInFeet
  }
  maxTerrainCache.set(level, max)
  return max
}

export interface RunwayPick {
  airport: Airport
  runway: Runway
  headingDeg: number
}

export function pickActiveRunwayEnd(airport: Airport, wind: number | null): RunwayPick | null {
  if (airport.runways.length === 0) return null
  // No wind info — fall back to heading1 of the first runway. MVP
  // behavior; Phase F adds a wind sim that picks dynamically.
  if (wind == null) {
    const r = airport.runways[0]
    if (!r) return null
    return { airport, runway: r, headingDeg: r.heading1 }
  }
  let best: { pick: RunwayPick; delta: number } | null = null
  for (const runway of airport.runways) {
    for (const h of [runway.heading1, runway.heading2]) {
      const delta = Math.abs((((wind - h + 540) % 360) - 180))
      if (!best || delta < best.delta) {
        best = { pick: { airport, runway, headingDeg: h }, delta }
      }
    }
  }
  return best?.pick ?? null
}

function findAirport(level: LevelData, name: string): Airport | undefined {
  return level.airports.find((a) => a.name === name)
}

/// Result of `pickRunwayForTakeoffGoal`. Captures everything the
/// takeoff queue needs to enqueue a slot without holding a reference
/// to the level — the eventual promotion path uses these fields
/// directly. `threshold` is projected at pick-time so the renderer
/// doesn't have to re-project every frame to position queue dots.
export interface RunwayForTakeoff {
  airportName: string
  runwayId: string
  runwayHeadingDeg: number
  threshold: { x: number; y: number }
}

/// Resolve a takeoff or SID-departure goal into the airport + runway
/// it would spawn from, with the threshold already projected.
/// Returns `null` if the goal references unknown airport/runway data —
/// the queue caller treats that as a soft-fail (skip this goal).
///
/// Takeoff goals are wind-driven (`pickActiveRunwayEnd`). Departure
/// (SID) goals fix the runway by the SID's published id. Mirrors the
/// branch logic in `spawnTakeoff` / `spawnDeparture` so the queue and
/// the legacy direct-spawn path resolve to the same runway end.
export function pickRunwayForTakeoffGoal(
  ctx: SpawnContext,
  goal: import('../types/LevelData').AircraftGoal,
): RunwayForTakeoff | null {
  if (goal.takeoff) {
    const airport = findAirport(ctx.level, goal.takeoff.airportName)
    if (!airport) return null
    const pick = pickActiveRunwayEnd(airport, ctx.effectiveWindDirection)
    if (!pick) return null
    const runwayId =
      pick.headingDeg === pick.runway.heading1 ? pick.runway.id1 : pick.runway.id2
    const thresholdGeo =
      pick.headingDeg === pick.runway.heading1
        ? pick.runway.startCoordinate
        : pick.runway.endCoordinate
    return {
      airportName: airport.name,
      runwayId,
      runwayHeadingDeg: pick.headingDeg,
      threshold: ctx.projector.project(thresholdGeo, true),
    }
  }
  if (goal.departure) {
    const airport = findAirport(ctx.level, goal.departure.airportName)
    if (!airport) return null
    for (const runway of airport.runways) {
      if (runway.id1 === goal.departure.runwayId) {
        return {
          airportName: airport.name,
          runwayId: runway.id1,
          runwayHeadingDeg: runway.heading1,
          threshold: ctx.projector.project(runway.startCoordinate, true),
        }
      }
      if (runway.id2 === goal.departure.runwayId) {
        return {
          airportName: airport.name,
          runwayId: runway.id2,
          runwayHeadingDeg: runway.heading2,
          threshold: ctx.projector.project(runway.endCoordinate, true),
        }
      }
    }
    return null
  }
  return null
}

/// Convert a screen-space vector (Y-down) into a magnetic heading
/// (0=N, clockwise).
function headingFromTo(from: { x: number; y: number }, to: { x: number; y: number }): number {
  const dx = to.x - from.x
  const dy = to.y - from.y
  const screenAngle = Math.atan2(dy, dx)
  let deg = (screenAngle * 180) / Math.PI + 90
  if (deg < 0) deg += 360
  if (deg >= 360) deg -= 360
  return deg
}
